import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { useTranslation } from "react-i18next"
import type { AnySceneObjectT, GroupStyleI, GroupStyleT, NumberInputT, SelectInputT } from "tweenly"
import { getStyleFieldLabel } from "../../../../helpers/properties.helpers"
import CompactSelect from "../CompactSelect"
import PrefixedInput from "../PrefixedInput"
import PropertyRow from "../PropertyRow"
import GroupFieldValue from "./GroupFieldValue"

interface Props {
    object: AnySceneObjectT
    groupStyle: GroupStyleI
    properties: GroupStyleT[]
    handleSetValue: (index: number, property: "property" | "value", value: any) => void
    handleDeleteGroup: (index: number) => void
    handleSetStyleValue?: (property: any, value: any) => void
    handleSetAttributeValue?: (property: any, value: any) => void
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * One filter or transform, laid out like a fill (see FillRow): its type and — when it is a
 * single number or a choice — its value side by side, with the remove button in the narrow
 * column every row ends in. A value that needs the room (a matrix, a drop shadow) takes the
 * rows below instead.
 */
const GroupFieldRow = ({
    groupStyle,
    properties,
    handleSetValue,
    handleDeleteGroup,
    handleSetStyleValue,
    handleSetAttributeValue,
}: Props) => {
    const { t } = useTranslation()

    const handleOnChange = (field: "property" | "value", value: any) => {
        handleSetValue(groupStyle.index, field, value)
    }
    const handleValueChange = (e: any) => handleOnChange("value", e.target.value)

    // an image fill is only offered to old graphics that already have one
    const options = properties
        .filter((property) =>
            groupStyle.property === "backgroundImage" ? true : property.input.label !== "image"
        )
        .map((property) => ({
            value: property.property,
            label: capitalize(property.input.label ?? property.property),
        }))

    const { type } = groupStyle.input
    const label = getStyleFieldLabel(groupStyle)
    const limits =
        type === "number" ? (groupStyle.input as NumberInputT).props?.inputProps : undefined

    const inlineValue =
        type === "number" ? (
            <PrefixedInput
                name={groupStyle.property}
                value={groupStyle.value}
                label={label}
                tooltip={label}
                handleOnChange={handleValueChange}
                min={limits?.min}
                max={limits?.max}
                step={limits?.step}
            />
        ) : type === "select" ? (
            <CompactSelect
                name={groupStyle.property}
                value={groupStyle.value}
                options={(groupStyle.input as SelectInputT).props.map((option: string) => ({
                    value: option,
                    label: option,
                }))}
                label={label}
                tooltip={label}
                handleOnChange={handleValueChange}
            />
        ) : null

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            {/* the remove button sits in the narrow column at the end, as in Figma */}
            <PropertyRow
                gutter={
                    <Tooltip title={`${t("editor:remove")} ${groupStyle.property}`}>
                        <IconButton
                            size="small"
                            aria-label={`${t("editor:remove")} ${groupStyle.property}`}
                            onClick={() => handleDeleteGroup(groupStyle.index)}
                        >
                            <RemoveRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
            >
                <Box display="flex" gap={1}>
                    <Box flex={1} minWidth={0}>
                        <CompactSelect
                            name="type"
                            value={groupStyle.property}
                            options={options}
                            label="type"
                            tooltip={`${capitalize(groupStyle.type)} type`}
                            handleOnChange={(e) => handleOnChange("property", e.target.value)}
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        {inlineValue}
                    </Box>
                </Box>
            </PropertyRow>
            {type === "string" && (
                <PropertyRow>
                    <PrefixedInput
                        name={groupStyle.property}
                        value={groupStyle.value}
                        type="text"
                        label={label}
                        tooltip={groupStyle.input.tooltipTitle ?? label}
                        handleOnChange={handleValueChange}
                    />
                </PropertyRow>
            )}
            {!inlineValue && type !== "string" && (
                // a drop shadow lays out its own rows; anything else gets one
                <Box display="flex" flexDirection="column" gap={1}>
                    {type === "shadow" ? (
                        <GroupFieldValue groupStyle={groupStyle} handleOnChange={handleOnChange} />
                    ) : (
                        <PropertyRow>
                            <GroupFieldValue
                                groupStyle={groupStyle}
                                handleOnChange={handleOnChange}
                                handleSetStyleValue={handleSetStyleValue}
                                handleSetAttributeValue={handleSetAttributeValue}
                            />
                        </PropertyRow>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default GroupFieldRow
